/**
 * src/components/ejecuciones/RevisionCompetidores.jsx
 * ¿La lista de competidores de cada tenant sigue siendo la correcta?
 *
 * Nace de una prueba de usuario (04-09-2026): el cliente dijo que sus
 * competidores reales no estaban en la lista y que los que estaban compartían muy
 * pocos productos con él. Las dos cosas eran ciertas y la causa era una:
 * `autoseed_competidores` siembra UNA vez, con un criterio de pura presencia, y no
 * vuelve a mirar nunca — para no pisar las ediciones del admin.
 *
 * ⚠️ 08-09-2026: este bloque YA NO es solo un diagnóstico. La lista se resiembra
 * sola cada 60 días, porque el motivo por el que no se aplicaba —«no pisar las
 * ediciones del admin»— lo asumió el campo `fijado` cuatro días antes: lo que pone
 * una persona nace fijado y el índice no lo toca; lo que pone el índice, sí.
 *
 * Esta pantalla se queda con las dos mitades: qué cambiaría AHORA (la propuesta) y
 * si el sistema va a cambiarlo solo (el distintivo de resiembra). La segunda es
 * nueva y nace de una queja concreta de Manuel —lanzó cuatro tenants, no vio ningún
 * cambio, y la única forma de saber por qué era abrir un job y leer el volcado del
 * pipeline—. Un tenant frenado a propósito no se puede leer igual que uno averiado.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Pildora } from '../ui/Pildora'
import { revisarCompetidores } from '../../api/competidoresRevision'
import { updateCliente } from '../../api/clientes'

const FILTROS = [
  { clave: 'cambios', etiqueta: 'Con cambios propuestos' },
  { clave: 'todos', etiqueta: 'Todos' },
]

const _pct = v => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`)

// El estado de la RESIEMBRA, que es distinto de la propuesta (10-09-2026). La
// propuesta dice «qué habría que cambiar»; esto dice «si el sistema lo va a
// cambiar solo, y cuándo lo hizo por última vez». Sin ello, un tenant frenado a
// propósito se lee exactamente igual que uno averiado — le pasó a Manuel con
// cuatro tenants seguidos, y la única forma de distinguirlo era abrir un job y
// leer el volcado del pipeline.
const RESIEMBRA = {
  al_dia:    { texto: 'Al día',     color: 'var(--color-ok)',
               ayuda: 'Revisada hace poco. No se volverá a tocar hasta que venza la cadencia.' },
  pendiente: { texto: 'Le toca',    color: 'var(--color-warn)',
               ayuda: 'Se resembrará sola en la próxima pasada de precios u OCs de este tenant.' },
  frenada:   { texto: 'Frenada',    color: 'var(--color-text-3)',
               ayuda: 'Tiene el freno puesto: no se resembrará aunque se lance el pipeline.' },
}

// ⚠️ El freno se pone y se quita DESDE AQUÍ (10-09-2026), y no es comodidad.
// Antes era `COMPETIDORES_RESIEMBRA_SOLO`, una variable de entorno del servicio
// worker — y esta pantalla la sirve el web, otro contenedor, que nunca la tuvo.
// Resultado: la chapa «Frenada» era INALCANZABLE y los tenants frenados se
// leyeron aquí como «Le toca» durante todo el despliegue por tandas. Ahora el
// freno es una columna del tenant, así que el mando y el indicador miran el
// mismo dato y no pueden discrepar.
const ACCION_FRENO = {
  frenada: { texto: 'Soltar', valor: false,
             ayuda: 'Quitar el freno: volverá a resembrarse cuando le toque.' },
  otro:    { texto: 'Frenar', valor: true,
             ayuda: 'Poner el freno: no se le tocará la lista aunque se lance el pipeline.' },
}

// «hace 2 horas» y no una fecha ISO: la pregunta delante de esta pantalla es
// «¿corrió ya?», no «¿qué día fue?».
function _hace(iso) {
  if (!iso) return 'nunca revisada'
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return 'revisada'
  const min = Math.floor(ms / 60000)
  if (min < 60) return `revisada hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 48) return `revisada hace ${h} h`
  return `revisada hace ${Math.floor(h / 24)} días`
}

export function RevisionCompetidores() {
  const navigate = useNavigate()
  const [datos, setDatos] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')
  const [filtro, setFiltro] = useState('cambios')
  const [abierto, setAbierto] = useState('')
  const [frenando, setFrenando] = useState('')

  const cargar = async () => {
    setCargando(true)
    setError('')
    try {
      setDatos(await revisarCompetidores())
    } catch (e) {
      // Un fallo de carga NO puede parecerse a «ningún tenant necesita cambios»:
      // esta pantalla existe para dar un veredicto y «no lo sé» hay que decirlo.
      setError(e?.response?.data?.detail || e?.message || 'No se pudo consultar la revisión')
      setDatos(null)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => { cargar() }, [])

  // Se recarga la lista entera al terminar, en vez de retocar la fila en local:
  // el estado que pinta la chapa (`frenada` / `pendiente` / `al_dia`) lo decide el
  // backend con las mismas funciones que usa el pipeline. Calcularlo aquí sería
  // una segunda copia de la regla, que es de lo que venimos.
  const alternarFreno = async (clienteId, valor) => {
    setFrenando(clienteId)
    setError('')
    try {
      await updateCliente(clienteId, { resiembra_frenada: valor })
      await cargar()
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message
               || `No se pudo cambiar el freno de ${clienteId}`)
    } finally {
      setFrenando('')
    }
  }

  const todos = datos?.tenants || []
  const filas = useMemo(() => (
    filtro === 'cambios'
      ? todos.filter(t => (t.proponer_alta?.length || 0) + (t.revisar_baja?.length || 0) > 0)
      : todos
  ), [todos, filtro])

  return (
    <Card
      title="Lista de competidores por tenant"
      subtitle="Compara los competidores que sigue cada cliente con los que hoy le disputan el negocio. Desde el 08-09-2026 la lista se resiembra sola cada 60 días: el distintivo de cada tenant dice si va a hacerlo y cuándo lo hizo."
      action={
        <Button variant="secondary" size="sm" onClick={cargar} loading={cargando}>
          Refrescar
        </Button>
      }
    >
      <div style={{
        display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap',
        alignItems: 'center', marginBottom: 'var(--space-4)',
      }}>
        {FILTROS.map(f => (
          <Pildora key={f.clave} activa={filtro === f.clave}
                   onClick={() => setFiltro(f.clave)}>
            {f.etiqueta}
          </Pildora>
        ))}
        {datos && (
          <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)',
                         color: datos.con_cambios ? 'var(--color-warn)' : 'var(--color-ok)' }}>
            {datos.con_cambios === 0
              ? `los ${datos.evaluados} tenants evaluados tienen la lista al día`
              : `${datos.con_cambios} de ${datos.evaluados} evaluados con cambios propuestos`}
          </span>
        )}
        {/* Un tenant que no se pudo medir NO es un tenant sin cambios. Meterlo en
            el mismo recuento es el cero que nadie midió. */}
        {datos?.sin_datos?.length > 0 && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
            · {datos.sin_datos.length} sin snapshot de precios, no evaluados
            ({datos.sin_datos.join(', ')})
          </span>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)',
                      marginBottom: 'var(--space-4)' }}>
          {error}
        </div>
      )}

      {!error && !cargando && todos.length === 0 && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
          No hay tenants activos que revisar.
        </div>
      )}

      {filas.length === 0 && todos.length > 0 && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
          Ningún tenant coincide con el filtro.
        </div>
      )}

      {filas.map(t => {
        const altas = t.proponer_alta || []
        const bajas = t.revisar_baja || []
        const desplegado = abierto === t.cliente_id
        return (
          <div key={t.cliente_id} style={{
            borderTop: '1px solid var(--color-border)', padding: 'var(--space-4) 0',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)',
                          flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate(`/clientes/${t.cliente_id}`)}
                title="Abrir la ficha del cliente (desde ahí se editan los competidores)"
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                  color: 'var(--color-text)', cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationColor: 'var(--color-border-2)', textUnderlineOffset: '3px',
                }}
              >
                {t.cliente_id}
              </button>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
                {t.tier} · sigue a {(t.vigilados || []).filter(v => v.activo).length} de {t.tope_tier}
              </span>
              {RESIEMBRA[t.resiembra] && (
                <span title={RESIEMBRA[t.resiembra].ayuda}
                      style={{ fontSize: 'var(--text-xs)', fontWeight: 600,
                               color: RESIEMBRA[t.resiembra].color,
                               border: `1px solid ${RESIEMBRA[t.resiembra].color}`,
                               borderRadius: '3px', padding: '1px 6px', cursor: 'help' }}>
                  {RESIEMBRA[t.resiembra].texto}
                </span>
              )}
              {(() => {
                const acc = ACCION_FRENO[t.resiembra === 'frenada' ? 'frenada' : 'otro']
                return (
                  <button
                    onClick={() => alternarFreno(t.cliente_id, acc.valor)}
                    disabled={frenando === t.cliente_id}
                    title={acc.ayuda}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                      cursor: frenando === t.cliente_id ? 'wait' : 'pointer',
                      textDecoration: 'underline',
                      textDecorationColor: 'var(--color-border-2)',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {frenando === t.cliente_id ? '…' : acc.texto}
                  </button>
                )
              })()}
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}
                    title={t.revisado_en || 'sin registro de resiembra'}>
                · {_hace(t.revisado_en)}
              </span>
              {t.estado === 'ok' ? (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
                  · mercado: {t.n_mercado} oferentes sobre {t.n_base} fichas
                  {t.base === 'catalogo' && ' (sin precios propios: solo presencia)'}
                </span>
              ) : (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warn)' }}>
                  · sin snapshot de precios: no evaluado
                </span>
              )}
            </div>

            {t.estado === 'ok' && (altas.length > 0 || bajas.length > 0) && (
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                {altas.length > 0 && (
                  <div style={{ color: 'var(--color-text-2)' }}>
                    <b style={{ color: 'var(--color-ok)' }}>Entrarían:</b>{' '}
                    {altas.slice(0, 3).map(a => (
                      `${a.nombre} (nº ${a.puesto}, te gana en el ${_pct(a.pre)} del catálogo)`
                    )).join(' · ')}
                    {altas.length > 3 && ` · y ${altas.length - 3} más`}
                  </div>
                )}
                {bajas.length > 0 && (
                  <div style={{ color: 'var(--color-text-2)', marginTop: 'var(--space-05)' }}>
                    <b style={{ color: 'var(--color-warn)' }}>Revisar:</b>{' '}
                    {bajas.slice(0, 3).map(b => (
                      b.puesto
                        ? `${b.nombre} (nº ${b.puesto}, solapa el ${_pct(b.cob)})`
                        : `${b.nombre} (${b.motivo})`
                    )).join(' · ')}
                    {bajas.length > 3 && ` · y ${bajas.length - 3} más`}
                  </div>
                )}
              </div>
            )}

            {t.estado === 'ok' && (t.vigilados || []).length > 0 && (
              <button
                onClick={() => setAbierto(desplegado ? '' : t.cliente_id)}
                style={{
                  background: 'none', border: 'none', padding: '4px 0 0',
                  fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                {desplegado ? 'ocultar' : 'ver los que sigue hoy y su puesto real'}
              </button>
            )}

            {desplegado && (
              <div style={{ overflowX: 'auto', marginTop: 'var(--space-2)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse',
                                fontSize: 'var(--text-xs)' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--color-text-3)' }}>
                      {['Competidor', 'Puesto', 'Solape', 'Te gana en', 'Es el más barato en', 'Estado']
                        .map(h => (
                          <th key={h} style={{ padding: '4px 8px', fontWeight: 500,
                                               whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(t.vigilados || []).map(v => (
                      <tr key={v.rut} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '4px 8px' }}>{v.nombre}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>
                          {v.puesto ? `nº ${v.puesto}` : '—'}
                        </td>
                        <td style={{ padding: '4px 8px' }}>{_pct(v.cob)}</td>
                        <td style={{ padding: '4px 8px' }}>{_pct(v.pre)}</td>
                        <td style={{ padding: '4px 8px' }}>{_pct(v.dom)}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--color-text-3)' }}>
                          {[!v.activo && 'inactivo', v.fijado && 'fijado', v.motivo]
                            .filter(Boolean).join(' · ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </Card>
  )
}
